import { useMemo } from 'react'
import { RefreshCw } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Button, MetricCard, MetricStrip } from '@/components/ui'
import {
  useCashPosition,
  useLowStockCount,
  useMarginTrend,
  useOpenRfqs,
  useRevenueMtd,
} from '../api/queries'

/**
 * Sprint 13b — Executive Overview page.
 *
 * Eight-card KPI grid. Five cards are wired against live endpoints,
 * three are explicit placeholders awaiting Finance modules in a
 * future sprint. Click-through routing where applicable.
 *
 * Layout follows the Dashboard archetype (`Foundation Docs/01`):
 * page header band on top + scrollable content well below. KPI grid
 * uses `MetricStrip variant="card"` with the `--metric-card-min-width`
 * token driving auto-fit columns.
 */
export function ExecutivePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const revenue = useRevenueMtd()
  const cash = useCashPosition()
  const lowStock = useLowStockCount()
  const rfqs = useOpenRfqs()
  const margin = useMarginTrend()

  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['executive'] })
  }

  const todayLabel = useMemo(() => {
    const now = new Date()
    return now.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    })
  }, [])

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-end justify-between gap-4 border-b border-border-subtle bg-surface px-6 py-3">
        <div className="flex flex-col gap-0.5">
          <h1 className="text-role-page-title leading-tight tracking-tight">
            Executive Overview
          </h1>
          <p className="text-role-section-label">{todayLabel}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={refreshAll}
          loading={
            revenue.isFetching ||
            cash.isFetching ||
            lowStock.isFetching ||
            rfqs.isFetching ||
            margin.isFetching
          }
        >
          <RefreshCw size={14} /> Refresh
        </Button>
      </header>

      <div className="flex min-w-0 flex-1 flex-col gap-6 overflow-auto bg-canvas p-6">
        <section>
          <h2 className="mb-2 text-role-section-label">Financials</h2>
          <MetricStrip>
            <ClickableCard onClick={() => navigate({ to: '/margins' })}>
              <MetricCard
                label="Revenue MTD"
                value={
                  revenue.isLoading || revenue.isError
                    ? '—'
                    : formatCurrency(revenue.data?.revenue ?? 0)
                }
                delta={
                  revenue.data
                    ? formatRevenueDelta(revenue.data.delta_pct)
                    : undefined
                }
                deltaTone={
                  revenue.data?.delta_pct === null ||
                  revenue.data?.delta_pct === undefined
                    ? 'neutral'
                    : revenue.data.delta_pct >= 0
                      ? 'positive'
                      : 'negative'
                }
              />
            </ClickableCard>

            <MetricCard
              label="Cash position"
              value={
                cash.isLoading || cash.isError ? '—' : formatCurrency(cash.data?.cash_position ?? 0)
              }
              delta={
                cash.data
                  ? `${cash.data.accounts.length} account${cash.data.accounts.length === 1 ? '' : 's'}`
                  : undefined
              }
              deltaTone="neutral"
            />

            <MetricCard
              label="P&L (current period)"
              value="—"
              delta="Coming in next finance sprint"
              deltaTone="neutral"
              valueTone="default"
            />

            <ClickableCard onClick={() => navigate({ to: '/margins' })}>
              <MetricCard
                label="Margin trend"
                value={
                  margin.isLoading || margin.isError
                    ? '—'
                    : margin.data?.currentPct !== null && margin.data?.currentPct !== undefined
                      ? `${margin.data.currentPct.toFixed(1)}%`
                      : '—'
                }
                delta={
                  margin.data
                    ? formatMarginDelta(margin.data.deltaPct)
                    : undefined
                }
                deltaTone={
                  margin.data?.deltaPct === null ||
                  margin.data?.deltaPct === undefined
                    ? 'neutral'
                    : margin.data.deltaPct >= 0
                      ? 'positive'
                      : 'negative'
                }
              />
            </ClickableCard>
          </MetricStrip>
        </section>

        <section>
          <h2 className="mb-2 text-role-section-label">Operations</h2>
          <MetricStrip>
            <ClickableCard
              onClick={() => navigate({ to: '/inventory' })}
            >
              <MetricCard
                label="Low stock"
                value={
                  lowStock.isLoading || lowStock.isError
                    ? '—'
                    : (lowStock.data ?? 0).toLocaleString()
                }
                delta={
                  lowStock.data && lowStock.data > 0
                    ? 'qty available ≤ 0'
                    : 'No items out of stock'
                }
                deltaTone={
                  lowStock.data && lowStock.data > 0 ? 'negative' : 'neutral'
                }
                valueTone={
                  lowStock.data && lowStock.data > 0 ? 'warning' : 'default'
                }
              />
            </ClickableCard>

            <ClickableCard
              onClick={() => navigate({ to: '/procurement' })}
            >
              <MetricCard
                label="Open RFQs"
                value={
                  rfqs.isLoading || rfqs.isError
                    ? '—'
                    : (rfqs.data?.open_rfq_count ?? 0).toLocaleString()
                }
                delta={
                  rfqs.data
                    ? `Across ${countDistinctVendors(rfqs.data.rfqs)} vendor${countDistinctVendors(rfqs.data.rfqs) === 1 ? '' : 's'}`
                    : undefined
                }
                deltaTone="neutral"
              />
            </ClickableCard>

            <MetricCard
              label="Pending invoices"
              value="—"
              delta="Coming in next finance sprint"
              deltaTone="neutral"
            />

            <MetricCard
              label="AR / AP ratio"
              value="—"
              delta="Coming in next finance sprint"
              deltaTone="neutral"
            />
          </MetricStrip>
        </section>
      </div>
    </div>
  )
}

/**
 * Wraps a MetricCard in a click-target. The MetricCard itself is a
 * pure data component (no interaction); this gives the executive
 * cards a way to drive navigation without inflating the primitive.
 */
function ClickableCard({
  children,
  onClick,
}: {
  children: React.ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer rounded-md text-left transition-colors duration-fast hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {children}
    </button>
  )
}

function formatCurrency(value: number): string {
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

function formatRevenueDelta(deltaPct: number | null): string {
  if (deltaPct === null) return 'No prior month data'
  const sign = deltaPct >= 0 ? '+' : ''
  return `${sign}${deltaPct.toFixed(1)}% vs last month`
}

function formatMarginDelta(deltaPct: number | null): string {
  if (deltaPct === null) return 'No comparable data'
  const sign = deltaPct >= 0 ? '+' : ''
  return `${sign}${deltaPct.toFixed(1)}pp vs last month`
}

function countDistinctVendors(
  rfqs: ReadonlyArray<{ vendor_id: number | null }>,
): number {
  const ids = new Set<number>()
  for (const r of rfqs) {
    if (r.vendor_id !== null) ids.add(r.vendor_id)
  }
  return ids.size
}
